import type { DraftContext } from '@/features/coach-review/composerDrafts';
import type { CoachingEffortTaskResponse, RecordCoachingEffortRequest, StartCoachingEffortRequest } from '@/lib/types';
import { readEffort, recordEffort, startEffort } from './api';
import { EffortClock } from './effortClock';
type Event = RecordCoachingEffortRequest['measurement'];
type WithoutCAS<T> = T extends Event ? Omit<T, 'client_operation_id' | 'expected_revision'> : never;
export type EffortEvent = WithoutCAS<Event>;
export type EditCategory = 'style' | 'factual' | 'action' | 'none';
/** Optional content-free session. Exact requests survive acknowledgment loss;
 * only telemetry is retried. A recovered revision never rebases a pending event. */
export class EffortSession {
  readonly clock = new EffortClock(performance.now());
  task: CoachingEffortTaskResponse | null = null;
  pending: RecordCoachingEffortRequest | null = null;
  frozen = false;
  private complete = true;
  error = false;
  private sending = false;
  private reviewed: string | null = null;
  private final: EffortEvent | null = null;
  private terminalOperation: string | null = null;
  private manualSample: ReturnType<EffortClock['sample']> | null = null;
  private manualCheckpointComplete: boolean | null = null;
  /** Deferred work stays explicit even if a late start/read succeeds. */
  get needsRetry() {
    if (!this.task) return !!this.manualSample || !!this.final;
    if (this.task.state !== 'open') return false;
    return (!!this.manualSample && !this.task.outcome && this.manualCheckpointComplete !== this.complete)
      || !!this.final;
  }
  constructor(readonly owner: DraftContext, readonly start: StartCoachingEffortRequest, private current: () => boolean, private notify: () => void) {}
  private async bounded<T>(work: (signal: AbortSignal) => Promise<T>) {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_resolve, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error("Measurement timeout")); }, 1500); });
    try { return await Promise.race([work(controller.signal), timeout]); } finally { clearTimeout(timer!); }
  }
  private changed() { if (this.current()) this.notify(); }
  async begin() {
    try {
      const response = await this.bounded(signal => startEffort(this.owner, this.start, signal));
      if (this.current()) { this.task = response.task; this.error = false; }
    } catch { if (this.current()) this.error = true; }
    this.changed();
  }
  async send(event: EffortEvent): Promise<boolean> {
    if (!this.current() || !this.task || this.pending) return false;
    this.pending = { measurement: { ...event, expected_revision: this.task.revision, client_operation_id: crypto.randomUUID() } as Event };
    return this.replayEvent();
  }
  private async replayEvent() {
    if (!this.current() || !this.task || !this.pending || this.sending) return false;
    const request = this.pending;
    this.sending = true;
    try {
      const response = await this.bounded(signal => recordEffort(this.owner, this.task!.id, request, signal));
      if (!this.current()) return false;
      this.task = response.task; this.pending = null; this.error = false;
      if (request.measurement.event === 'review') this.reviewed = `${request.measurement.draft_id}:${request.measurement.reviewed_revision}`;
      if (this.manualSample && request.measurement.event === 'checkpoint') {
        const checkpoint = request.measurement;
        if ((['active_ms', 'wall_ms', 'paused_ms', 'pause_count'] as const)
          .every(key => checkpoint[key] === this.manualSample![key])) this.manualCheckpointComplete = checkpoint.complete;
      }
      this.changed(); return true;
    } catch { if (this.current()) this.error = true; this.changed(); return false; }
    finally { this.sending = false; this.changed(); }
  }
  domainFailed() { this.complete = false; this.changed(); }
  private async checkpointManual() {
    if (!this.manualSample || this.task?.outcome || this.task?.state === 'finished') return true;
    if (!this.task || this.pending) return false;
    if (this.manualCheckpointComplete === this.complete) return true;
    return this.send({ event: 'checkpoint', epoch_id: this.start.epoch_id, ...this.manualSample, complete: this.complete });
  }
  async beforeSave(draft?: { id: string; revision: number }) {
    if (!this.current()) return;
    if (this.start.mode === 'manual') {
      // The first domain attempt ends manual timing even if starting or
      // checkpointing the optional measurement has lost its acknowledgment.
      if (!this.manualSample) {
        const now = performance.now();
        this.frozen = true; this.clock.setAllowed(false, now);
        this.manualSample = this.clock.sample(now);
        this.changed();
      }
      await this.checkpointManual();
      return;
    }
    if (!this.current() || !this.task || this.task.state !== 'open') return;
    if (this.pending) return; // Domain work proceeds; unresolved telemetry is separate.
    if (draft && this.reviewed === `${draft.id}:${draft.revision}` && this.frozen && this.complete) return;
    this.frozen = true; this.clock.setAllowed(false, performance.now());
    if (!await this.send({ event: 'checkpoint', epoch_id: this.start.epoch_id, ...this.clock.sample(performance.now()), complete: this.complete })) return;
    if (draft) await this.send({ event: 'review', draft_id: draft.id, reviewed_revision: draft.revision });
  }
  /** Called only after a real successful domain receipt. Optional failure cannot
   * reject the author's save callback or create publication/observation history. */
  async received(event: Extract<EffortEvent, { event: 'outcome' }>) {
    if (!this.current()) return;
    const artifactId = event.artifact?.kind === 'feedback' ? event.artifact.receipt.feedback_id : event.artifact?.source_id;
    const operation = event.domain_operation_id ?? `manual:${event.artifact?.kind}:${artifactId}`;
    if (this.terminalOperation && this.terminalOperation !== operation) return;
    if (!this.terminalOperation) { this.terminalOperation = operation; this.final = structuredClone(event); }
    if (!this.task) { this.changed(); return; }
    if (this.task.outcome) return;
    if (this.pending) { this.changed(); return; }
    if (!await this.checkpointManual()) return;
    if (await this.send(this.final!)) await this.send({ event: 'finish' });
  }
  async retry() {
    if (!this.current()) return;
    if (!this.task) await this.begin();
    if (!this.current() || !this.task) return;
    if (this.pending && !await this.replayEvent()) return;
    if (!await this.checkpointManual()) return;
    if (this.final && !this.task.outcome) { if (!await this.send(this.final)) return; }
    if (this.task.outcome && this.task.state === 'open') await this.send({ event: 'finish' });
  }
  async recover() {
    if (!this.current()) return;
    try {
      const response = await readEffort(this.owner, this.start.task_id);
      if (this.current() && !this.pending) { this.task = response.task; this.error = false; }
    } catch { if (this.current()) this.error = true; }
    this.changed();
  }
}
